import * as queryString from 'query-string';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { UrlParamKeys } from '~commonInfra/url/UrlParamKeys';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { contentCountsUpToDate } from '~src/spacesPlatform/actions/ContentCountsActions';
import { enterSpace, setSpaceInfoItem } from '~src/spacesPlatform/actions/EnterSpaceAction';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { getDefaultOrderForContentTypesOrSpaceInfo } from '~src/spacesPlatform/models/sort/SortOptionDefinitions';
import { IOrderClause } from '~src/spacesPlatform/serverApi/types/IOrderClause';

import { SessionSiteSelectors } from '../../bootstrap/redux/selectors/SessionInfoSelectors';
import { ConnectedQueryParamUpdateProvider, QueryParamUpdateProviderOwnProps } from '../../bootstrap/routerHelpers/QueryParamUpdateProvider';
import { RouterError } from '../../bootstrap/routerHelpers/RouterError';
import { Messages } from '../../codegen/Localize';
import { BrowserTitleService } from '../../commonInfra/services/BrowserTitleService';
import { setOrderAction } from '../../spacesPlatform/actions/SetOrderAction';
import { updateBreadcrumbs } from '../../spacesPlatform/actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '../../spacesPlatform/components/breadcrumbs/BreadcrumbStateInfo';
import { getOrderClausesFromQueryParam } from '../../spacesPlatform/components/Explorer/ExplorerUrlBindings';
import { ConnectedSpaceInfoProvider } from '../../spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpacesSiteUrls } from '../../spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ExplorableItemType } from '../../spacesPlatform/models/ContentType';
import { NavLocationSelectors } from '../../spacesPlatform/selectors/NavigationStateSelectors';
import { ScheduleSubspaceName, scheduleSubspaceNameToExplorableItemType, SpaceInfo } from '../../spacesPlatform/state/SpaceInfo';
import { SchedulesService } from '../resources/Schedules';
import { IScheduleInfo } from '../types/IScheduleInfo';
import { getSchedulesPlaceBreadcrumb } from './AllSchedulesSpaceProvider';
import { ConnectedScheduleSpace } from './ConnectedScheduleSpace';
import { ScheduleSpaceOwnProps } from './ScheduleSpace';

type TitleName = {[P in ScheduleSubspaceName]: () => string };
const Titles: TitleName = {
  details: Messages.details,
  flows: Messages.flows,
  extractRefreshes: Messages.extractTasks,
  subscriptions: Messages.subscriptions
};

export interface ScheduleSpaceProviderStateProps {
  readonly siteId: string;
  readonly inServerArea: boolean;
}

export interface ScheduleSpaceProviderDispatchProps {
  readonly enterSpace: (spaceInfo: SpaceInfo) => void;
  readonly setSpaceInfoItem: (spaceInfo: SpaceInfo) => void;
  readonly requestSpaceContents: () => void;
  readonly contentCountsUpToDate: () => void;
  readonly setOrderAction: (order: ReadonlyArray<IOrderClause>) => void;
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
}

export interface ScheduleSpaceProviderOwnProps {
  readonly siteName: string;
  readonly subspaceName: ScheduleSubspaceName;
  readonly scheduleId: string;
  readonly routeLocationSearch: string;
  readonly urls: SpacesSiteUrls | ServerUrls;
  readonly renderSpace: (props: ScheduleSpaceOwnProps) => JSX.Element;
}

export type ScheduleSpaceProviderComponentProps = ScheduleSpaceProviderOwnProps & ScheduleSpaceProviderStateProps & ScheduleSpaceProviderDispatchProps;

export interface ScheduleSpaceProviderState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly schedule: IScheduleInfo;
}

export class ScheduleSpaceProviderComponent extends React.PureComponent<ScheduleSpaceProviderComponentProps, ScheduleSpaceProviderState> {

  private unmounted: boolean = false;
  private readonly initialState: ScheduleSpaceProviderState = {
    initialRender: true,
    renderChildren: false,
    hasError: false,
    schedule: undefined,
  };

  public constructor(props: ScheduleSpaceProviderComponentProps) {
    super(props);
    this.state = this.initialState;
  }

  public componentDidMount() {
    this.getData();
  }

  public render() {
    if (this.state.renderChildren) {
      const props: ScheduleSpaceOwnProps = {
        urls: this.props.urls,
        reloadSpace: this.reloadSpace,
      };
      return this.props.renderSpace(props);
    } else if (this.state.hasError) {
      if (this.props.inServerArea) {
        return <RouterError errorType={'server-schedule'} urls={undefined} />;
      } else if (this.props.urls instanceof SpacesSiteUrls) {
        return <RouterError errorType={'site-schedule'} urls={this.props.urls} />;
      }
    }
    return null;
  }

  private getData = async () => {
    try {
      let schedule = undefined;
      if (this.props.inServerArea) {
        schedule = await SchedulesService.getById(this.props.scheduleId);
      } else {
        schedule = await SchedulesService.getByIdForSite(this.props.scheduleId, this.props.siteId);
      }
      if (schedule) {
        this.updateState({schedule});
        this.props.updateBreadcrumbs([getSchedulesPlaceBreadcrumb(this.props.urls), {text: schedule.name}]);
      } else {
        this.updateState({hasError: true});
      }
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  public componentDidUpdate(prevProps: ScheduleSpaceProviderComponentProps) {
    if (this.scheduleChanged(prevProps)) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.changeSubspace();
      this.setState({initialRender: false, renderChildren: true});

    } else if (this.props.subspaceName !== prevProps.subspaceName) {
      this.changeSubspace();
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private changeSubspace() {
    this.props.enterSpace(
      {
        item: this.state.schedule,
        spaceType: 'schedule',
        subspaceName: this.props.subspaceName
      }
    );

    this.props.contentCountsUpToDate();

    BrowserTitleService.setTitleWithSpaceEntityNameAndTranslatedSubspaceName({
      spaceEntityName: this.state.schedule.name,
      translatedSubspaceName: Titles[this.props.subspaceName]()
    });

    if (this.props.subspaceName === 'details') {
      return;
    }

    this.clearAndSetSortOrderFromURL();

    this.props.requestSpaceContents();
  }

  private clearAndSetSortOrderFromURL() {
    const contentTypes = [scheduleSubspaceNameToExplorableItemType(this.props.subspaceName) as ExplorableItemType];
    const spaceInfo = {item: undefined, spaceType: 'schedule', subspaceName: this.props.subspaceName} as SpaceInfo;
    const defaultOrder = getDefaultOrderForContentTypesOrSpaceInfo(contentTypes, spaceInfo);
    const values = queryString.parse(this.props.routeLocationSearch);

    const orderToSet = getOrderClausesFromQueryParam(values[UrlParamKeys.Order], defaultOrder);
    this.props.setOrderAction(orderToSet);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.schedule);
  }

  private scheduleChanged(prevProps: ScheduleSpaceProviderComponentProps): boolean {
    return !_.isUndefined(this.props.siteName) &&
      !_.isUndefined(this.props.scheduleId) &&
      (prevProps.siteName !== this.props.siteName || prevProps.scheduleId !== this.props.scheduleId);
  }

  private updateState<K extends keyof ScheduleSpaceProviderState>(newState: Pick<ScheduleSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private reloadSpace = async () => {
    await this.getData();
    this.props.setSpaceInfoItem(
      {
        item: this.state.schedule,
        spaceType: 'schedule',
        subspaceName: this.props.subspaceName
      }
    );
  };
}

const mapStateToProps: MapStateToProps<ScheduleSpaceProviderStateProps> = (appState) => {
  return {
    siteId: SessionSiteSelectors.siteId(appState),
    inServerArea: NavLocationSelectors.inServerArea(appState)
  };
};

const mapDispatchToProps: MapDispatchToProps<ScheduleSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  enterSpace,
  setSpaceInfoItem,
  requestSpaceContents,
  contentCountsUpToDate,
  updateBreadcrumbs,
  setOrderAction,
}, dispatch);

export const ConnectedScheduleSpaceProvider = connect(mapStateToProps, mapDispatchToProps)(ScheduleSpaceProviderComponent);

const renderSpace = (props: ScheduleSpaceOwnProps): JSX.Element => <ConnectedScheduleSpace {...props} />;

export interface LocationProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export type ScheduleSpaceProviderProps = LocationProps & Omit<ScheduleSpaceProviderOwnProps, 'renderSpace'>;

export function ScheduleSpaceProvider(props: ScheduleSpaceProviderProps) {
  const queryUpdaterProps: QueryParamUpdateProviderOwnProps = {
    routeLocationPath: props.routeLocationPath,
    routeLocationSearch: props.routeLocationSearch,
    shouldUpdateFilters: false,
    shouldUpdateContentTypes: false,
    shouldUpdateSort: true,
    shouldUpdateBreadcrumbs: false
  };

  const spaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'schedule',
    subspaceName: props.subspaceName
  };

  const spaceProps = {
    siteName: props.siteName,
    scheduleId: props.scheduleId,
    subspaceName: props.subspaceName,
    routeLocationSearch: props.routeLocationSearch,
    urls: props.urls,
    renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedQueryParamUpdateProvider {...queryUpdaterProps}>
        <ConnectedScheduleSpaceProvider {...spaceProps} />
      </ConnectedQueryParamUpdateProvider>
    </ConnectedSpaceInfoProvider>
  );
}
